import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Badge, Alert, InputGroup } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaKey, FaUsers, FaSearch, FaTimes, FaSort, FaBook } from "react-icons/fa";
import { permissionGroupService } from "../../services/permissionGroup.service";
import moment from "moment";
import GroupPermissionsTab from "./GroupPermissionsTab";
import UserGroupsTab from "./UserGroupsTab";
import GroupRunbooksTab from "./GroupRunbooksTab";

function PermissionGroupsTab({ user, hideTitle }) {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showRunbooksModal, setShowRunbooksModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formData, setFormData] = useState({
    groupName: "",
    groupDescription: "",
    active: true,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGroups();
  }, [user.clientId, user.clientGuid]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const params = {
        clientId: user.clientId,
        clientGuid: user.clientGuid,
        // Don't send active parameter - let backend return all groups
      };

      const response = await permissionGroupService.getAll(params);
      const groupData = response.data || response;

      setGroups(groupData);
      setFilteredGroups(groupData);
      setError(null);
    } catch (err) {
      setError("Failed to load permission groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = groups;

    // Apply search filter
    if (searchTerm) {
      filtered = groups.filter(
        (group) =>
          group.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.groupDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          group.groupId?.toString().includes(searchTerm)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        if (sortConfig.key === 'createdOn') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase() || '';
          bValue = bValue?.toLowerCase() || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredGroups(filtered);
  }, [searchTerm, groups, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };


  const handleAdd = () => {
    setEditingGroup(null);
    setFormData({ groupName: "", groupDescription: "", active: true });
    setShowModal(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      groupName: group.groupName,
      groupDescription: group.groupDescription,
      active: group.active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingGroup) {
        await permissionGroupService.update({
          ...formData,
          groupId: editingGroup.groupId,
          clientId: editingGroup.clientId,
          clientGuid: editingGroup.clientGuid,
        });
      } else {
        await permissionGroupService.create({
          ...formData,
          clientId: user.clientId,
          clientGuid: user.clientGuid,
        });
      }
      setShowModal(false);
      loadGroups();
    } catch (err) {
      setError("Failed to save permission group");
    }
  };

  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      await permissionGroupService.remove(groupToDelete.groupId);
      setShowDeleteModal(false);
      setGroupToDelete(null);
      if (selectedGroup?.groupId === groupToDelete.groupId) {
        setSelectedGroup(null);
      }
      loadGroups();
    } catch (err) {
      setError("Failed to delete permission group");
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search permission groups by name, description, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
              <FaTimes />
            </Button>
          )}
        </InputGroup>
      </div>

      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded" data-tour="group-actions">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedGroup}
          onClick={() => setShowPermissionsModal(true)}
        >
          <FaKey className="mb-1 me-2" />
          Manage Permissions
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedGroup}
          onClick={() => setShowUsersModal(true)}
        >
          <FaUsers className="mb-1 me-2" />
          Manage Users
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedGroup}
          onClick={() => setShowRunbooksModal(true)}
        >
          <FaBook className="mb-1 me-2" />
          Manage Runbooks
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedGroup}
          onClick={() => handleEdit(selectedGroup)}
        >
          <FaEdit className="mb-1 me-2" />
          Edit
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedGroup}
          onClick={() => handleDeleteClick(selectedGroup)}
        >
          <FaTrash className="mb-1 me-2" />
          Delete
        </Button>
        <div className="ms-auto">
          <Button variant="outline-primary" size="sm" onClick={handleAdd} data-tour="add-group-btn">
            <FaPlus className="mb-1 me-2" />
            Add Group
          </Button>
        </div>
      </div>

      <div
        className="table-responsive"
        style={
          hideTitle
            ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto" }
            : { maxHeight: "600px", overflowY: "auto" }
        }
        data-tour="permissions-grid"
      >
        <table className="table table-hover table-sm">
          <thead>
            <tr>
              <th className="text-nowrap">
                <Button
                  variant="link"
                  className="text-decoration-none text-dark ps-0 fw-bold"
                  onClick={() => handleSort('groupId')}
                >
                  Group ID
                  <FaSort className="ms-2 mb-1 text-primary" />
                </Button>
              </th>
              <th className="text-nowrap">
                <Button
                  variant="link"
                  className="text-decoration-none text-dark ps-0 fw-bold"
                  onClick={() => handleSort('groupName')}
                >
                  Name
                  <FaSort className="ms-2 mb-1 text-primary" />
                </Button>
              </th>
              <th className="text-nowrap">
                <Button
                  variant="link"
                  className="text-decoration-none text-dark ps-0 fw-bold"
                  onClick={() => handleSort('groupDescription')}
                >
                  Description
                  <FaSort className="ms-2 mb-1 text-primary" />
                </Button>
              </th>
              <th className="text-nowrap">
                <Button
                  variant="link"
                  className="text-decoration-none text-dark ps-0 fw-bold"
                  onClick={() => handleSort('createdOn')}
                >
                  Created On
                  <FaSort className="ms-2 mb-1 text-primary" />
                </Button>
              </th>
              <th className="text-nowrap">
                <Button
                  variant="link"
                  className="text-decoration-none text-dark ps-0 fw-bold"
                  onClick={() => handleSort('active')}
                >
                  Status
                  <FaSort className="ms-2 mb-1 text-primary" />
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((group) => (
              <tr
                key={group.groupId}
                onClick={() => setSelectedGroup(group)}
                className={selectedGroup?.groupId === group.groupId ? "table-primary" : ""}
                style={{
                  cursor: "pointer"
                }}
              >
                <td>{group.groupId}</td>
                <td>{group.groupName}</td>
                <td>{group.groupDescription}</td>
                <td>{moment(group.createdOn).format("MM/DD/YYYY")}</td>
                <td>
                  <Badge bg={group.active ? "success" : "secondary"}>
                    {group.active ? "Active" : "Inactive"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingGroup ? "Edit" : "Add"} Permission Group
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.groupName}
                onChange={(e) =>
                  setFormData({ ...formData, groupName: e.target.value })
                }
                placeholder="Enter group name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.groupDescription}
                onChange={(e) =>
                  setFormData({ ...formData, groupDescription: e.target.value })
                }
                placeholder="Enter group description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Permissions - {selectedGroup?.groupName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && <GroupPermissionsTab user={user} preSelectedGroup={selectedGroup} />}
        </Modal.Body>
      </Modal>

      <Modal
        show={showUsersModal}
        onHide={() => setShowUsersModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Users - {selectedGroup?.groupName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && <UserGroupsTab user={user} preSelectedGroup={selectedGroup} />}
        </Modal.Body>
      </Modal>

      <Modal
        show={showRunbooksModal}
        onHide={() => setShowRunbooksModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Runbooks - {selectedGroup?.groupName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedGroup && <GroupRunbooksTab user={user} preSelectedGroup={selectedGroup} />}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FaTrash className="me-2 mb-1" />
            Delete Permission Group
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>

          <p>Are you sure you want to delete the following permission group?</p>

          {groupToDelete && (
            <div className="bg-light p-3 rounded">
              <div className="mb-2">
                <strong>Group ID:</strong> {groupToDelete.groupId}
              </div>
              <div className="mb-2">
                <strong>Group Name:</strong> {groupToDelete.groupName}
              </div>
              {groupToDelete.groupDescription && (
                <div className="mb-2">
                  <strong>Description:</strong> {groupToDelete.groupDescription}
                </div>
              )}
              <div className="mb-2">
                <strong>Status:</strong>{' '}
                <Badge bg={groupToDelete.active ? "success" : "secondary"}>
                  {groupToDelete.active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="mb-0">
                <strong>Created:</strong> {moment(groupToDelete.createdOn).format("MM/DD/YYYY h:mm A")}
              </div>
            </div>
          )}

          <Alert variant="info" className="mt-3 mb-0">
            <small>
              <strong>Note:</strong> Deleting this group will remove all associated user memberships,
              permissions, and runbook access assignments.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setGroupToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <FaTrash className="me-1 mb-1" />
            Delete Group
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PermissionGroupsTab;
